import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [orderInfo, setOrderInfo] = useState(null);
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const hasPolled = useRef(false);
  
  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;
    
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      toast.error('Invalid checkout session');
      navigate(tenantPath('/cart'));
      return;
    }
    
    pollPaymentStatus(sessionId);
  }, [searchParams, navigate, tenantPath]);
  
  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    
    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    
    try {
      const response = await api.get(`/checkout/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setOrderInfo(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'checking' && (
            <>
              <Loader className="h-16 w-16 text-[#334155] mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Processing Payment...
              </h1>
              <p className="text-gray-600">Please wait while we confirm your payment</p>
            </>
          )}
          
          {status === 'success' && (
            <div data-testid="success-message">
              <CheckCircle className="h-16 w-16 text-[#334155] mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Order Confirmed!
              </h1>
              <p className="text-gray-600 text-lg mb-8">
                Thank you for your order. We'll start building your custom equipment right away.
              </p>
              {orderInfo && (
                <div className="bg-gray-50 p-6 rounded-lg border border-[#F3F4F6] mb-8">
                  <p className="text-gray-600 text-sm mb-2">Order ID</p>
                  <p className="text-gray-900 font-mono text-lg">{orderInfo.order_id}</p>
                </div>
              )}
              <button
                onClick={() => navigate(tenantPath('/dashboard'))}
                className="h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all"
                data-testid="view-orders-button"
              >
                View My Orders
              </button>
            </div>
          )}
          
          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <h1 className="text-3xl font-bold text-[#EF4444] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Payment Failed
              </h1>
              <p className="text-gray-600 mb-8">
                {status === 'timeout' && 'Payment verification timed out. Please check your email for confirmation.'}
                {status === 'expired' && 'Payment session expired. Please try again.'}
                {status === 'error' && 'An error occurred while processing your payment.'}
              </p>
              <button
                onClick={() => navigate(tenantPath('/cart'))}
                className="h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all"
              >
                Return to Cart
              </button>
            </>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
